import type { FC, PropsWithChildren } from 'react'
import { useEffect, useRef, useState } from 'react'

import type { css } from 'twind/css'

import { useVisibilityObserver } from '@core/hooks/useVisibilityObserver'
import { useTwindContext } from '@core/twind'

interface Props {
  enabled?: boolean
  trackingFn: () => void
  overrideCSS?: ReturnType<typeof css> | string | string[]
}

/**
 * A wrapper div for triggering impression tracking
 */
export const ImpressionContainer: FC<PropsWithChildren<Props>> = ({
  trackingFn,
  enabled = true,
  children,
  overrideCSS,
}) => {
  const { tw } = useTwindContext()

  const [isVisible, setVisible] = useState<boolean>(false)
  const trackedRef = useRef(false)
  const visibilityObserverRef = useVisibilityObserver<HTMLDivElement>({
    onChange: setVisible,
    threshold: [0.7],
  })

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>
    if (enabled) {
      timeout = setTimeout(() => {
        if (isVisible && !trackedRef.current) {
          trackedRef.current = true
          trackingFn()
        }
      }, 1000)
    }
    return () => clearTimeout(timeout)
  }, [enabled, isVisible, trackingFn])

  return (
    <div
      style={{
        minWidth: '1px',
        minHeight: '1px',
      }}
      ref={visibilityObserverRef}
      className={tw?.(overrideCSS)}
    >
      {children}
    </div>
  )
}
